import { msg } from './i18n/translate';
import { MARK_EVERY, marksForLevel, nextMarkLevel, PET_MAX_LEVEL } from './pet-stars';
export const PET_SPECIES = [
  'otter', 'rabbit', 'penguin', 'fox', 'corgi',
  'cat', 'red-panda', 'panda', 'capybara', 'owl',
  'squirrel', 'parrot', 'toucan', 'flamingo', 'bee',
  'butterfly', 'seal', 'pufferfish', 'axolotl', 'octopus',
  // Sea, air and land companions added 2026-09-30 (migration 0035).
  'dolphin', 'sea-turtle', 'whale', 'jellyfish', 'seahorse', 'clownfish', 'crab',
  'eagle', 'hummingbird', 'dragonfly', 'ladybug', 'swan', 'peacock',
  'tiger', 'lion', 'elephant', 'giraffe', 'deer', 'koala', 'hedgehog',
] as const;
export const CLASS_PET_SPECIES = ['otter', 'rabbit', 'penguin'] as const;

export type PetSpecies = typeof PET_SPECIES[number];
export type PetMode = 'class' | 'individual';
/**
 * D-259 (2026-11-03): the whole-class shared pet is retired for new classes. Every new
 * class is 一人一寵; existing shared classes keep working and can only switch to
 * individual. New features do not add shared-mode behaviour (docs/DECISIONS.md D-259).
 */
export const NEW_CLASS_PET_MODE = 'individual' satisfies PetMode;
/** How a class's individual pets arrive (D-103): children claim them, or the forest assigns one. */
export type PetPickMode = 'student' | 'random';

/** Habitat groups for the companion picker; together they list every species once. */
export const PET_SPECIES_HABITATS: Record<'land' | 'water' | 'sky', readonly PetSpecies[]> = {
  land: ['rabbit', 'fox', 'corgi', 'cat', 'red-panda', 'panda', 'capybara', 'squirrel', 'hedgehog', 'deer', 'koala', 'tiger', 'lion', 'elephant', 'giraffe'],
  water: ['otter', 'penguin', 'seal', 'axolotl', 'pufferfish', 'octopus', 'clownfish', 'seahorse', 'crab', 'jellyfish', 'sea-turtle', 'dolphin', 'whale'],
  sky: ['owl', 'parrot', 'toucan', 'flamingo', 'bee', 'butterfly', 'ladybug', 'dragonfly', 'hummingbird', 'swan', 'peacock', 'eagle'],
};

// Species names are translation keys (msg): the web app shows t(name), the Worker
// translatorFor(locale).t(name). A new species needs one entry here plus its English
// in src/i18n/messages/en/server.json.
export const PET_SPECIES_NAMES: Record<PetSpecies, string> = {
  otter: msg('水獺'), rabbit: msg('兔子'), penguin: msg('企鵝'), fox: msg('狐狸'),
  corgi: msg('柯基'), cat: msg('貓咪'), 'red-panda': msg('小熊貓'), panda: msg('大熊貓'),
  capybara: msg('水豚'), owl: msg('貓頭鷹'),
  squirrel: msg('松鼠'), parrot: msg('鸚鵡'), toucan: msg('巨嘴鳥'),
  flamingo: msg('紅鶴'), bee: msg('蜜蜂'), butterfly: msg('蝴蝶'),
  seal: msg('海豹'), pufferfish: msg('河豚'), axolotl: msg('美西螈'), octopus: msg('章魚'),
  dolphin: msg('海豚'), 'sea-turtle': msg('海龜'), whale: msg('鯨魚'), jellyfish: msg('水母'),
  seahorse: msg('海馬'), clownfish: msg('小丑魚'), crab: msg('螃蟹'),
  eagle: msg('老鷹'), hummingbird: msg('蜂鳥'), dragonfly: msg('蜻蜓'), ladybug: msg('瓢蟲'),
  swan: msg('天鵝'), peacock: msg('孔雀'),
  tiger: msg('老虎'), lion: msg('獅子'), elephant: msg('大象'), giraffe: msg('長頸鹿'),
  deer: msg('小鹿'), koala: msg('無尾熊'), hedgehog: msg('刺蝟'),
};

// Default class-pet names (also translation keys). Changing species renames only a pet
// still carrying one of the built-in defaults — in any language — so a teacher's custom
// name is kept.
export const COMPANION_DEFAULT_NAMES: Record<PetSpecies, string> = { ...PET_SPECIES_NAMES, otter: msg('小森'), rabbit: msg('絨絨'), penguin: msg('團團') };
export const BUILT_IN_CLASS_PET_NAMES = [msg('小森'), msg('絨絨'), msg('團團'), msg('苔苔')] as const;

/** Default class-pet name for a species in the given translator's language. */
export const companionDefaultName = (species: PetSpecies, t: (source: string) => string = (source) => source) =>
  t(COMPANION_DEFAULT_NAMES[species]);

/** The built-in default names in Chinese plus each given translator's language (English "Mori"…). */
export const builtInClassPetNames = (...translators: Array<(source: string) => string>): string[] =>
  [...new Set([...BUILT_IN_CLASS_PET_NAMES, ...translators.flatMap((t) => BUILT_IN_CLASS_PET_NAMES.map((source) => t(source)))])];

// The two curves are fixed for the entire growth cycle. Changing roster size
// changes the class total, never the difficulty of an already started level.
export const classPetThresholdScale = 20;
export const PET_FORM_LEVELS = [1, 10, 20, 30] as const;

// Hatch points (customer batch, migration 0063 class_preferences.pet_hatch_points): the points a companion needs to
// hatch. The whole curve scales by hatch / 10, so 20 hatches at 20 and still reaches Lv.30 at twice the points. The
// default 10 is the original curve. (class_preferences.growth_speed from 0005 and pet_growth_cycles.hatch_threshold /
// level_curve_json are old columns nothing reads; do not add a second source for this.)
export const DEFAULT_HATCH_POINTS = 10;
export const MIN_HATCH_POINTS = 10;
export const MAX_HATCH_POINTS = 50;
export const HATCH_POINTS_STEP = 5;
export const isHatchPoints = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= MIN_HATCH_POINTS && value <= MAX_HATCH_POINTS && value % HATCH_POINTS_STEP === 0;
/** The hatch points of a class-like object (the snapshot's `class`, a row); anything unusable reads as the default. */
export const hatchOf = (klass?: { pet_hatch_points?: number | null } | null): number =>
  isHatchPoints(klass?.pet_hatch_points) ? klass!.pet_hatch_points! : DEFAULT_HATCH_POINTS;

// D-253: the curve keeps going past Lv.30 (the final form) up to PET_MAX_LEVEL, same formula, so a mark every 5 levels
// costs a little more each time: Lv.35 at 758 points, Lv.50 at 1,455, Lv.70 at 2,735, Lv.90 at 4,415 (hatch 10). Forms still stop at Lv.30.
export function petThreshold(level: number, mode: PetMode, hatch: number = DEFAULT_HATCH_POINTS): number {
  const index = Math.max(0, Math.min(PET_MAX_LEVEL - 1, Math.floor(level) - 1));
  const individual = 10 + index * 5 + Math.floor(index * index / 2);
  return Math.round(individual * (mode === 'class' ? classPetThresholdScale : 1) * hatch / DEFAULT_HATCH_POINTS);
}

export function petLevelForPoints(points: number, mode: PetMode, hatch: number = DEFAULT_HATCH_POINTS) {
  const safePoints = Math.max(0, Math.floor(points));
  let level = 0;
  for (let candidate = 1; candidate <= PET_MAX_LEVEL; candidate++) {
    if (safePoints < petThreshold(candidate, mode, hatch)) break;
    level = candidate;
  }
  const visualStage = level >= 30 ? 30 : level >= 20 ? 20 : level >= 10 ? 10 : level >= 1 ? 1 : 0;
  return {
    level,
    visualStage,
    nextThreshold: level >= PET_MAX_LEVEL ? null : petThreshold(level + 1, mode, hatch),
    status: level === 0 ? 'egg' as const : 'active' as const,
  };
}

/** D-253: the points total of the next star for a pet drawn at its final form, or null (not final yet, or every star earned). */
export function nextMarkPoints(level: number, visualStage: number, mode: PetMode, hatch: number = DEFAULT_HATCH_POINTS): number | null {
  const star = visualStage >= 30 ? nextMarkLevel(level) : null;
  return star === null ? null : petThreshold(star, mode, hatch);
}

/** D-258: the points total where the current mark began (Lv.30 before the first star), for the progress bar toward the next one;
 *  null when nextMarkPoints is null. */
export function markFromPoints(level: number, visualStage: number, mode: PetMode, hatch: number = DEFAULT_HATCH_POINTS): number | null {
  return visualStage >= 30 && nextMarkLevel(level) !== null ? petThreshold(30 + marksForLevel(level) * MARK_EVERY, mode, hatch) : null;
}

/**
 * An individual pet's growth state. A pet its child has not claimed yet (no species, D-103) stays
 * an egg whatever its points: no form, badge or evolution until it is claimed, then it appears at
 * the form its points have reached.
 */
export function individualPetState(points: number, claimed: boolean, hatch: number = DEFAULT_HATCH_POINTS) {
  const state = petLevelForPoints(points, 'individual', hatch);
  return claimed ? state : { ...state, visualStage: 0, status: 'egg' as const };
}

/**
 * D-179: a companion grows with a child's points — rewards minus reminders, an undone action gone
 * (`points_earned`) — and never with what was spent in the shop. Redeeming only lowers 可兌換
 * (`points_available`), which still guards redeeming and undoing (insufficient_undo_balance). Every
 * pet level, form, "points to next" text and ranking reads this, never `points_available`
 * (src/growth-guard.test.ts).
 */
export const growthPointsOf = (student: { points_earned?: number | null }): number => Number(student.points_earned ?? 0);

/** A class pet's growth: the enrolled children's points, a negative balance counting as 0 so one child's minus never eats the others'. */
export function classGrowthPoints(students: readonly { points_earned?: number | null }[]): number {
  return students.reduce((total, student) => total + Math.max(0, growthPointsOf(student)), 0);
}
